import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaPlane, 
  FaGraduationCap, 
  FaUsers, 
  FaGlobeEurope,
  FaCheckCircle,
  FaClock,
  FaHandshake,
  FaShieldAlt
} from 'react-icons/fa';
import { motion } from 'framer-motion';

const Services = () => {
  const services = [
    {
      icon: <FaPlane className="text-3xl" />,
      title: 'Tourist Visas',
      description: 'Short-term visas for leisure travel, sightseeing, and visiting friends/family.',
      features: [
        'Single & Multiple Entry',
        'Express Processing',
        'Travel Insurance',
        'Itinerary Planning'
      ],
      color: 'bg-blue-100 text-blue-600',
      path: '/services/tourist'
    },
    {
      icon: <FaGraduationCap className="text-3xl" />,
      title: 'Student Visas',
      description: 'Visa assistance for students pursuing education abroad.',
      features: [
        'University Admission Support',
        'Financial Documentation',
        'Post-Study Work Options',
        'Dependent Visas'
      ],
      color: 'bg-green-100 text-green-600',
      path: '/services/student'
    },
    {
      icon: <FaGlobeEurope className="text-3xl" />,
      title: 'Schengen Visas',
      description: 'Access 26 European countries with a single visa.',
      features: [
        'Multiple Country Itineraries',
        'Travel Medical Insurance',
        'Hotel Bookings',
        'Flight Reservations'
      ],
      color: 'bg-purple-100 text-purple-600',
      path: '/services/schengen'
    },
    {
      icon: <FaUsers className="text-3xl" />,
      title: 'Family Visas',
      description: 'Reunite families with spouse, parent, and dependent visas.',
      features: [
        'Spouse/Partner Visas',
        'Parent Visas',
        'Document Verification',
        'Permanent Residence Pathway'
      ],
      color: 'bg-pink-100 text-pink-600',
      path: '/services/family'
    },
  ];

  const processSteps = [
    {
      icon: <FaHandshake className="text-2xl" />,
      title: 'Consultation',
      description: 'Free initial assessment of your visa requirements'
    },
    {
      icon: <FaCheckCircle className="text-2xl" />,
      title: 'Documentation',
      description: 'We help prepare all required documents'
    },
    {
      icon: <FaShieldAlt className="text-2xl" />,
      title: 'Application',
      description: 'Submission and follow-up with authorities'
    },
    {
      icon: <FaClock className="text-2xl" />,
      title: 'Processing',
      description: 'Track your application in real-time'
    },
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Our Visa Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive visa solutions for all your travel and immigration needs
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            >
              <div className={`p-6 ${service.color} flex justify-center`}>
                <div className="p-4 bg-white rounded-xl">
                  {service.icon}
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>
                
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center text-sm text-gray-700">
                      <FaCheckCircle className="mr-2 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Link
                  to={service.path}
                  className="block text-center btn-primary py-2"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Process Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Simple 4-Step Process
          </h3>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 h-1 w-3/4 bg-primary-200 top-6"></div>
            
            <div className="grid lg:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative text-center"
                >
                  <div className="relative z-10">
                    <div className="w-12 h-12 mx-auto mb-4 bg-primary-600 rounded-full flex items-center justify-center text-white">
                      {step.icon}
                    </div>
                    <div className="w-8 h-8 mx-auto -mt-4 bg-white rounded-full flex items-center justify-center text-primary-600 font-bold border-4 border-gray-50">
                      {index + 1}
                    </div>
                    <h4 className="text-xl font-semibold mt-4 mb-2 text-gray-900">
                      {step.title}
                    </h4>
                    <p className="text-gray-600">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/process" className="btn-secondary">
              View Detailed Process
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;